import {
  DefaultTheme,
  LinkingOptions,
  NavigationContainer,
  NavigationProp,
  NavigatorScreenParams,
  useNavigation,
} from '@react-navigation/native'
import { createStackNavigator, StackNavigationProp, StackScreenProps } from '@react-navigation/stack'
import * as ExpoLinking from 'expo-linking'
import { Box, Center, HStack, Spinner, Text, VStack } from 'native-base'
import QueryString from 'qs'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Linking, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { ConsultationStatus } from '../@types'
import CustomHeaderBackButton from '../components/CustomHeaderBackButton/CustomHeaderBackButton'
import { LogoutWarning } from '../components/LogoutWarning'
import { useIdleDetection } from '../components/LogoutWarning/use-idle-detection'
import { Maintenance } from '../components/Maintenance/Maintenance'
import { VideoCall } from '../components/VideoCall'
import { I18N_DEBUG, REFRESH_INTERVAL_MS, APP_URL } from '../constants/config'
import { useAuth } from '../context/auth'
import { ContactMethod, useConfig } from '../context/config'
import { useConsultations } from '../data/use-consultations'
import { colors } from '../design-system/theme/partners/tokens'
import { useTracking } from '../hooks/tracking/use-tracking'
import { useScreenOptions, useWhiteScreenOptions } from '../hooks/use-screen-options'
import { navigationRef } from '../lib/refs'
import AuthRedirect from '../screens/AuthRedirect'
import { LandingPageRedirect } from '../screens/LandingPageRedirect'
import { Language } from '../screens/Language'
import { PrivacyPolicy } from '../screens/PrivacyPolicy'
import { Referral } from '../screens/Referral'
import { AppTermsAndConditions, SubscriptionTermsAndConditions } from '../screens/TermsAndConditions'
import Welcome from '../screens/Welcome'
import { isUserAuthenticated } from '../util/auth'
import { isWeb } from '../util/platform'
import { getSessionId } from '../util/sentry'

import { authStackLinking, AuthStackParamList } from './Auth'
import { MainTabParamList, mainTabsLinking } from './MainTabs'
import styles from './styles'

const Auth = React.lazy(() => import('./Auth'))
const MainTabs = React.lazy(() => import('./MainTabs'))

export type RootStackParamList = {
  Welcome: { reason?: string; onLoginSuccess?: () => void } | undefined
  Auth: NavigatorScreenParams<AuthStackParamList> | undefined
  Main: NavigatorScreenParams<MainTabParamList> | undefined
  PrivacyPolicy: undefined
  TermsAndConditions: undefined
  SubscriptionTermsAndConditions: undefined
  Language: undefined
  AuthRedirect: { reason?: string } | undefined
  Referral: { slug: string }
  LandingPageRedirect: { companySlug: string; token: string }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace ReactNavigation {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface RootParamList extends RootStackParamList {}
  }
}

export type RootStackScreenProps<T extends keyof RootStackParamList> = StackScreenProps<RootStackParamList, T>

export type RootStackNavigationProp<T extends keyof RootStackParamList> = StackNavigationProp<RootStackParamList, T>

const RootStack = createStackNavigator<RootStackParamList>()

const linking: LinkingOptions<RootStackParamList> = {
  // Should add APP_URL to 'prefixes' array, see https://reactnavigation.org/docs/deep-linking/#setup-with-expo-projects.
  // However, only the links here in RootStackParamList (like /terms-and-conditions) work; others links in
  // mainTabsLinking and authStackLinking don't.
  prefixes: [APP_URL],
  config: {
    screens: {
      Main: mainTabsLinking,
      Auth: authStackLinking,
      Welcome: '/welcome',
      TermsAndConditions: '/terms-and-conditions',
      PrivacyPolicy: '/privacy-policy',
      Language: '/language',
      AuthRedirect: '/redirect',
      Referral: '/referral/:slug',
      LandingPageRedirect: '/lp/:companySlug',
    },
  },
}

// @see https://github.com/react-navigation/react-navigation/issues/9811
const navTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: 'transparent',
  },
}

export function Navigation() {
  const { t } = useTranslation()
  const screenOptions = useScreenOptions()
  const { trackPageView } = useTracking()
  const whiteScreenOptions = useWhiteScreenOptions()
  const whiteScreenOptionsNoHeader = useWhiteScreenOptions(true)
  const { isReady, profile, setContinueNavigationUrlPath } = useAuth()
  const routeNameRef = useRef<string>()
  const previousRouteNameRef = useRef<string>()
  const { config } = useConfig()
  const { data: consultationsResponse } = useConsultations(
    {
      status: [ConsultationStatus.ACTIVE, ConsultationStatus.QUEUED],
      pageSize: 100,
    },
    {
      refreshInterval: REFRESH_INTERVAL_MS,
    }
  )
  const { userInteractionHandler, reset } = useIdleDetection(
    config.patientSessionLengthInMins,
    config.warningBeforePatientLogoutInMins
  )

  const onNavigationReady = () => {
    Linking.getInitialURL()
      .then(async url => {
        if (!url) return

        const { queryParams, path } = ExpoLinking.parse(url)

        setContinueNavigationUrlPath(path || '')

        if (path && Object.keys(queryParams ?? {}).length > 0) {
          navigationRef.setParams(queryParams as QueryString.ParsedQs)
        }

        if (isWeb()) {
          return
        }

        return navigationRef.reset({
          index: 0,
          routes: [
            {
              name: 'AuthRedirect',
            },
          ],
        })
      })
      .catch(err => console.error('An error occurred while getting/parsing the initial URL', err))
  }

  const getHeaderBackButton = (navigation: NavigationProp<RootStackParamList>, callRedirect?: VoidFunction) => (
    <HStack>
      <CustomHeaderBackButton
        callRedirect={callRedirect ?? (() => navigation.navigate('Main'))}
        navigation={navigation}
      />
    </HStack>
  )

  if (!isReady) {
    return (
      <Center height="100%" accessibilityRole="progressbar">
        <Spinner />
      </Center>
    )
  }

  if (config.maintenanceMode) {
    return <Maintenance emergencyPhoneNumber={config.emergencyPhoneNumber} />
  }

  return (
    // eslint-disable-next-line react-native/no-inline-styles
    <View style={{ flex: 1 }} {...(userInteractionHandler ? userInteractionHandler.panHandlers : {})}>
      {/* Enable video calls from any screen */}
      {config.availableContactMethods.includes(ContactMethod.VIDEO) &&
        consultationsResponse?.consultations.map(consultation => (
          <VideoCall
            key={consultation.id}
            videoCallId={typeof consultation.videoCallId === 'number' ? String(consultation.videoCallId) : undefined}
          />
        ))}
      <NavigationContainer
        linking={linking}
        theme={navTheme}
        ref={navigationRef}
        onReady={() => {
          onNavigationReady()
        }}
        onStateChange={async () => {
          const previousRouteName = routeNameRef.current
          const currentRouteName = navigationRef?.getCurrentRoute()?.name || 'unknown'

          if (previousRouteName !== currentRouteName) {
            trackPageView()
          }

          // Save the current route name for later comparison
          routeNameRef.current = currentRouteName
          previousRouteNameRef.current = previousRouteName
        }}
      >
        <LogoutWarning reset={reset} />
        <SessionDebugger />
        <RootStack.Navigator
          initialRouteName={isUserAuthenticated({ profile }) ? 'AuthRedirect' : 'Welcome'}
          screenOptions={{ ...screenOptions, headerShown: false }}
        >
          <RootStack.Screen name="Main" component={MainTabs} />
          {!isUserAuthenticated({ profile }) && <RootStack.Screen name="Auth" component={Auth} />}
          {!isUserAuthenticated({ profile }) && (
            <RootStack.Screen
              name="Welcome"
              component={Welcome}
              options={({ navigation }) => ({
                ...whiteScreenOptionsNoHeader,
                headerLeft: () => (isWeb() ? getHeaderBackButton(navigation, () => history.back()) : null),
              })}
            />
          )}
          <RootStack.Screen
            name="Referral"
            component={Referral}
            options={({ navigation }) => ({
              ...whiteScreenOptionsNoHeader,
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <RootStack.Screen
            name="TermsAndConditions"
            component={AppTermsAndConditions}
            options={({ navigation }) => ({
              ...whiteScreenOptions,
              headerTitleStyle: styles.headerTitle,
              title: t('screens.termsAndConditions.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <RootStack.Screen
            name="SubscriptionTermsAndConditions"
            component={SubscriptionTermsAndConditions}
            options={({ navigation }) => ({
              ...whiteScreenOptions,
              headerTitleStyle: styles.headerTitle,
              title: t('screens.subscriptionTermsAndConditions.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />
          <RootStack.Screen
            name="PrivacyPolicy"
            component={PrivacyPolicy}
            options={({ navigation }) => ({
              ...whiteScreenOptions,
              headerTitleStyle: styles.headerTitle,
              title: t('screens.privacyPolicy.title'),
              headerLeft: () => getHeaderBackButton(navigation),
            })}
          />

          {I18N_DEBUG && <RootStack.Screen name="Language" component={Language} />}
          <RootStack.Screen name="AuthRedirect" component={AuthRedirect} />
          <RootStack.Screen name="LandingPageRedirect" component={LandingPageRedirect} />
        </RootStack.Navigator>
      </NavigationContainer>
    </View>
  )
}

const SessionDebugger = () => {
  const { top } = useSafeAreaInsets()
  const [sessionId, setSessionId] = useState('')
  const [ip, setIp] = useState('')
  const [url, setUrl] = useState<{ name: string; params: Record<string, string> }>({ name: '', params: {} })
  const auth = useAuth()
  const navigation: any = useNavigation()

  useEffect(() => {
    const updateSessionId = async () => {
      setSessionId(await getSessionId())
      getIp()
    }
    updateSessionId()
  }, [auth.profile])

  useEffect(() => {
    const unsubscribe = navigation.addListener('state', () => {
      setUrl(navigation.getCurrentRoute())
    })

    return unsubscribe
  }, [navigation])

  const getIp = async () => {
    const res = await fetch('https://api.ipify.org/?format=json')
    const resJson = await res.json()
    setIp(resJson?.ip)
  }

  if (!auth.profile?.showDebugInfo) {
    return null
  }

  return (
    <Box backgroundColor={colors.bg}>
      <VStack alignItems="stretch" maxWidth="600px" width="full" alignSelf="center" px={4} pt={top}>
        <Text>{`Session ID: ${sessionId}`}</Text>
        <Text>{`Time: ${new Date().toISOString()}`}</Text>
        <Text>{`Screen: ${url.name}`}</Text>
        <Text>{`Params: ${JSON.stringify(url.params ?? {})}`}</Text>
        <Text>{`IP: ${ip}`}</Text>
      </VStack>
    </Box>
  )
}

export default Navigation
